import { useEffect, useMemo } from 'react'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import determineNextLoyaltyTier from 'loyalty/lib/determineNextLoyaltyTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { isSnackbarShown, markSnackbarAsShown } from 'loyalty/storage/tierProgressSnackbar'
import pluralize from 'lib/string/pluralize'
import { useAppSelector } from 'hooks/reduxHooks'

const VIEW_PROGRESS_ACTION = Object.freeze({
  label: 'View progress',
  to: `/${LOYALTY_LANDING_PAGE}#benefits`,
})

function buildSnackbarMessage(loyaltyAccount: App.LoyaltyAccount): string | undefined {
  const nextTier = determineNextLoyaltyTier(loyaltyAccount.tier)

  if (!nextTier) return undefined

  const statusCredits = loyaltyAccount.statusCredits ?? 0
  const totalCreditsForTierUpgrade = loyaltyAccount.totalCreditsForTierUpgrade ?? 0
  const numberOfNights = loyaltyAccount.numberOfNights ?? 0
  const numberOfNightsForTierUpgrade = loyaltyAccount.numberOfNightsForTierUpgrade ?? 0

  const creditsRemaining = Math.ceil(totalCreditsForTierUpgrade - statusCredits)
  const nightsRemaining = Math.ceil(numberOfNightsForTierUpgrade - numberOfNights)

  if (creditsRemaining > 50) {
    return undefined
  }
  if (creditsRemaining <= 0 && nightsRemaining <= 0) {
    return undefined
  }

  const nextTierName = getLoyaltyTierDisplayName(nextTier)

  if (!nextTierName) return undefined

  const requirements: Array<string> = []

  if (creditsRemaining > 0) {
    const { text: creditsLabel } = pluralize({ count: creditsRemaining, singular: 'status credit', plural: 'status credits' })
    requirements.push(`${creditsRemaining} more ${creditsLabel}`)
  }

  if (nightsRemaining > 0) {
    const { text: nightsLabel } = pluralize({ count: nightsRemaining, singular: 'night' })
    requirements.push(`${nightsRemaining} more ${nightsLabel}`)
  }
  return `You're so close to ${nextTierName} Société status! Only ${requirements.join(' and ')} to go.`
}

interface Params {
  memberId?: string
}

function useLoyaltyTierProgressSnackbar(params: Params) {
  const { memberId } = params

  const loyaltyAccountState = useAppSelector(state => state.loyalty?.account)

  const isInitial = loyaltyAccountState?.initial
  const isFetching = loyaltyAccountState?.fetching
  const loyaltyAccount = loyaltyAccountState?.data

  const snackbarMessage = useMemo(() => {
    if (isInitial || isFetching) return undefined
    if (!loyaltyAccount) return undefined

    return buildSnackbarMessage(loyaltyAccount)
  }, [isFetching, isInitial, loyaltyAccount])

  useEffect(() => {
    if (!memberId) return
    if (!snackbarMessage) return

    if (isSnackbarShown(memberId)) {
      return
    }

    showSnackbar(snackbarMessage, 'success', { action: VIEW_PROGRESS_ACTION })
    markSnackbarAsShown(memberId)
  }, [memberId, snackbarMessage])
}

export default useLoyaltyTierProgressSnackbar
